"use client";

import { useCallback, useEffect, useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { getTemplatesForMode, TEMPLATE_REGISTRY } from "@assets/templates";
import ExamplesPanel from "@demo/features/examples";
import FeaturedCarousel from "@demo/features/examples/FeaturedCarousel";
import { useFeaturedTemplates } from "@demo/features/examples/useFeaturedTemplates";
import { useTemplateSelect } from "@demo/features/examples/useTemplateSelect";
import { useMode } from "@demo/hooks/useMode";
import { analytics, ANALYTICS_EVENTS } from "@demo/libs/analytics";
import { useDetectStore } from "@demo/store/detect";

// Matches ExamplesPanel's exit transition; keep in sync so it finishes animating before unmount.
const EXAMPLES_PANEL_EXIT_MS = 300;

/** Keep a panel mounted for `durationMs` after it closes so its exit transition can play. */
function useDeferredUnmount(isOpen: boolean, durationMs: number): boolean {
  const [shouldRender, setShouldRender] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      return;
    }
    const timer = setTimeout(() => setShouldRender(false), durationMs);
    return () => clearTimeout(timer);
  }, [isOpen, durationMs]);
  return shouldRender;
}

/**
 * Detect's examples: an inline featured strip (floating just above the category input) plus
 * the full "see all" panel. Selecting an example pre-fills the detect surface via
 * `applyDetectExample` (see `useTemplateSelect`). The strip is shown only while the surface is
 * untouched. The gallery remains accessible when no examples are featured.
 */
const DetectExamples = () => {
  const [mode] = useMode();
  const featured = useFeaturedTemplates(mode);

  const hasImage = useDetectStore((state) => !!state.image);
  const hasCategories = useDetectStore((state) => state.categories.length > 0);
  const hasPending = useDetectStore((state) => state.pendingCategory.trim().length > 0);
  const isEmpty = !hasImage && !hasCategories && !hasPending;

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const isPanelMounted = useDeferredUnmount(isPanelOpen, EXAMPLES_PANEL_EXIT_MS);

  const closeAfterSelect = useCallback(() => setIsPanelOpen(false), []);
  const handleSelect = useTemplateSelect(closeAfterSelect);

  // Close the panel once the surface is touched (an example applied, a manual upload, typing…).
  useEffect(() => {
    if (!isEmpty) setIsPanelOpen(false);
  }, [isEmpty]);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
    analytics.track(ANALYTICS_EVENTS.EXAMPLES_PANEL_OPENED, { source: "see_all_button" });
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen((wasOpen) => {
      if (wasOpen) analytics.track(ANALYTICS_EVENTS.EXAMPLES_PANEL_CLOSED, { source: "close_button" });
      return false;
    });
  }, []);

  if (getTemplatesForMode(TEMPLATE_REGISTRY.templates, mode).length === 0) return null;

  const stripVisible = isEmpty && !isPanelOpen;

  return (
    <>
      <div className="relative">
        <div
          className={cn(
            "absolute inset-x-0 bottom-2 transition-[opacity,transform] duration-200 ease-out",
            stripVisible ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0",
          )}
          inert={!stripVisible}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-foreground-primary body">{strings.templates.title}</span>
              <Button
                className="text-foreground-secondary legal"
                size="sm"
                type="button"
                variant="ghost"
                onClick={openPanel}
              >
                <Icon aria-hidden="true" className="size-4" name="layout-grid" />
                <span className="ml-1">{strings.templates.seeAllLabel}</span>
              </Button>
            </div>
            <FeaturedCarousel templates={featured} variant="inline" onSelect={handleSelect} />
          </div>
        </div>
      </div>
      {isPanelMounted && (
        <ExamplesPanel isVisible={isPanelOpen && isEmpty} onClose={closePanel} onTemplateSelect={closeAfterSelect} />
      )}
    </>
  );
};

export default DetectExamples;
